import includes from 'lodash/includes'

import { trackError, trackUserEvent } from '@vend/utilities'
import { EventEmitter } from '../util/event-emitter'
import { WEB_REGISTER } from '../components/web-register.constant'

const POST_MESSAGE_SOURCE = 'Vend Client API'

const CLIENT_API_METHOD_MAP: Record<string, string> = {
  'add-product': 'addProduct',
  'add-unknown-product': 'addUnknownProduct',
  'add-customer': 'addCustomer',

  // @deprecated Legacy API methods
  'product-add': 'addLegacyProduct',
  'customer-add': 'addLegacyCustomer',
}

const PUBLISHED_EVENTS = {
  SALE_COMPLETE: 'sale:complete',
  SALE_DISCARD: 'sale:discard',
  SALE_UPDATE: 'sale:update',
  OUTLET_SET: 'outlet:set',
  REGISTER_SET: 'register:set',
}

export interface ApiEvent {
  method: string
  params: $TSFixMe
  event: MessageEvent
}

type Events = {
  apiEvent: [ApiEvent]
}

/**
 * Responsible for consuming and emitting events related to the client API.
 *
 * @class ClientApiEventBus
 * @constructor
 * @extends EventEmitter
 *
 * @module clientApiEventBus
 */
export class ClientApiEventBus extends EventEmitter<Events> {
  private _onPostMessage: $TSFixMe

  // ============================== Public API ============================== //

  /**
   * Constants for the various events that can be emitted as part of the client API.
   *
   * @readOnly
   * @property EVENTS
   * @type Object
   */
  public get EVENTS() {
    return PUBLISHED_EVENTS
  }

  /**
   * The "source" that the client API will report all events to be from.
   *
   * @readOnly
   * @property POST_MESSAGE_SOURCE
   * @type String
   */
  public get POST_MESSAGE_SOURCE() {
    return POST_MESSAGE_SOURCE
  }

  /**
   * Initialise the Client API Event Bus.
   *
   * @method init
   */
  public init() {
    if (!this._onPostMessage) {
      this._onPostMessage = (event: $TSFixMe) => this._handlePostMessage(event)
      window.addEventListener('message', this._onPostMessage)
    }
  }

  /**
   * Emits a client API event. Events are emitted as a post message to the parent window if webregister is being
   * framed.
   *
   * @method emitEvent
   *
   * @param  params {Object}
   *
   *         @param params.eventName {String}
   *                The name of the event to emit
   *
   *         @param [params.data] {Any}
   *                @todo
   */
  public emitEvent({ eventName, data }: { eventName: string; data?: any }) {
    const target = window === window.top ? window : window.parent

    if (!includes(PUBLISHED_EVENTS, eventName)) {
      trackError('[Client API] Attempt to emit non-published event', {
        data: { eventName },
      })
      return
    }

    const payload = {
      event_name: eventName,
      data,
      source: POST_MESSAGE_SOURCE,
    }

    target.postMessage(JSON.stringify(payload), '*')
  }

  /**
   * Tracks and logs the given error message and posts the message to the event origin.
   *
   * @method logError
   *
   * @param  event {Object}
   *         The original event the error is related to
   *
   * @param  message {String}
   *         The error message to log/emit
   */
  public logError(event: any, message: string) {
    const data = JSON.stringify({
      source: POST_MESSAGE_SOURCE,
      error: message,
    })

    if (event && event.source) {
      event.source.postMessage(data, event.origin)
    }

    // eslint-disable-next-line no-console
    console.warn(`[Client API] ${message}`, {
      error: message,
      data: event.data,
    })
    trackUserEvent(WEB_REGISTER.DISPLAY_NAME, 'Client API Error', {
      error: message,
      data: event.data,
    })
  }

  // ============================== Private API ============================== //

  /**
   * Parses the method and params out of the post message event, and invokes the respective method.
   *
   * @private
   * @method _handlePostMessage
   *
   * @param  event {Event}
   *         The post message event.
   */
  private _handlePostMessage(event: MessageEvent) {
    let data
    try {
      data = JSON.parse(event.data) || {}
    } catch (e) {
      // event.data did not contain valid JSON and so is probably not Client API related
      return
    }

    const { origin } = event
    const { method, params, source } = data
    const apiMethodName = CLIENT_API_METHOD_MAP[method]

    if (origin !== window.origin) {
      return
    }

    // Only continue if the event has a method param that we are aware of. This is to prevent us processing events
    // that are not Client API related (e.g. Payment API)
    if (apiMethodName) {
      if (origin !== window.origin) {
        trackUserEvent(
          WEB_REGISTER.DISPLAY_NAME,
          'Client API Unmatched Origin',
          {
            origin: window.origin,
            messageOrigin: origin,
            data,
          }
        )
      }
      // Swallow event if it came from Webregister Client API, otherwise we risk getting into a spinning cycle of
      // death when the error post message event is thrown and caught by itself.
      if (source && source === POST_MESSAGE_SOURCE) {
        return
      }

      trackUserEvent(WEB_REGISTER.DISPLAY_NAME, 'Client API', {
        params,
        method,
      })

      this.emit('apiEvent', {
        method: apiMethodName,
        params,
        event,
      })
    }
  }
}

export const clientApiEventBus = new ClientApiEventBus()
